/**
 * ForecastingFilterBar
 * Compact shared filter bar for the Forecasting module.
 * Multi-select Brand, Category, Store, City + collapsible date range.
 * Sticky at top of content area.
 */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  Badge,
  Collapse,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQuery } from '@tanstack/react-query';
import { useForecastingFilters } from './ForecastingFilterContext';
import { fetchFilterOptions, StoreOption } from '../../services/filtersApi';

// ─── Shared field style (matches existing design system) ───
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '12px',
    minHeight: 32,
    bgcolor: 'var(--color-surface)',
    borderRadius: '4px',
    fontFamily: 'var(--font-body)',
    '& fieldset': { borderColor: 'var(--color-border)' },
    '&:hover fieldset': { borderColor: 'var(--color-accent)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-accent)', borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-body)',
    '&.Mui-focused': { color: 'var(--color-accent)' },
  },
  '& .MuiAutocomplete-clearIndicator': { color: 'var(--color-text-muted)' },
  '& .MuiAutocomplete-popupIndicator': { color: 'var(--color-text-muted)' },
};

const chipSx = {
  bgcolor: 'var(--color-accent-bg)',
  color: 'var(--color-accent-hover)',
  borderColor: 'var(--color-accent)',
  height: 22,
  fontSize: '10px',
  fontWeight: 500,
  fontFamily: 'var(--font-body)',
  '& .MuiChip-deleteIcon': { color: 'var(--color-accent)', fontSize: 13 },
};

const ForecastingFilterBar: React.FC = () => {
  const {
    brands, categories, stores, cities, dateRange,
    setBrands, setCategories, setStores, setCities, setDateRange,
    clearAll, activeFilterCount,
  } = useForecastingFilters();

  const [showDateRange, setShowDateRange] = useState(false);

  // Fetch filter option data (single API call, cached 5 min)
  const { data: filterData, isLoading } = useQuery({
    queryKey: ['forecasting-filter-options'],
    queryFn: () => fetchFilterOptions(),
    staleTime: 5 * 60 * 1000,
  });

  const brandOptions: string[] = filterData?.brands ?? [];
  const categoryOptions: string[] = filterData?.categories ?? [];
  const cityOptions: string[] = filterData?.cities ?? [];

  // Cascading: filter stores by selected cities
  const storeOptions: StoreOption[] = useMemo(() => {
    const allStores = filterData?.stores ?? [];
    if (cities.length === 0) return allStores;
    return allStores.filter((s) => s.city && cities.includes(s.city));
  }, [filterData?.stores, cities]);

  // When cities change and selected stores no longer match, prune them
  const validStoreIds = useMemo(() => new Set(storeOptions.map((s) => s.store_id)), [storeOptions]);
  const activeStores = useMemo(() => stores.filter((id) => validStoreIds.has(id)), [stores, validStoreIds]);
  // Sync if pruning happened
  React.useEffect(() => {
    if (activeStores.length !== stores.length) {
      setStores(activeStores);
    }
  }, [activeStores, stores, setStores]);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        borderRadius: '6px 6px 0 0',
        mb: 2,
      }}
    >
      {/* Main filter row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          flexWrap: 'wrap',
        }}
      >
        {/* Filter icon with badge */}
        <Tooltip title="Filters">
          <Badge badgeContent={activeFilterCount} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}>
            <FilterIcon sx={{ fontSize: 18, color: 'var(--color-text-muted)' }} />
          </Badge>
        </Tooltip>

        {/* Brand multi-select */}
        <Autocomplete
          multiple
          size="small"
          options={brandOptions}
          value={brands}
          onChange={(_, val) => setBrands(val)}
          loading={isLoading}
          renderInput={(params) => (
            <TextField {...params} label="Brand" variant="outlined" sx={fieldSx} />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option} label={option} size="small" sx={chipSx} />
            ))
          }
          sx={{ minWidth: 180, flex: '1 1 160px', maxWidth: 280 }}
          limitTags={2}
        />

        {/* Category multi-select */}
        <Autocomplete
          multiple
          size="small"
          options={categoryOptions}
          value={categories}
          onChange={(_, val) => setCategories(val)}
          loading={isLoading}
          renderInput={(params) => (
            <TextField {...params} label="Category" variant="outlined" sx={fieldSx} />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option} label={option} size="small" sx={chipSx} />
            ))
          }
          sx={{ minWidth: 180, flex: '1 1 160px', maxWidth: 280 }}
          limitTags={2}
        />

        {/* City multi-select */}
        <Autocomplete
          multiple
          size="small"
          options={cityOptions}
          value={cities}
          onChange={(_, val) => setCities(val)}
          loading={isLoading}
          renderInput={(params) => (
            <TextField {...params} label="City" variant="outlined" sx={fieldSx} />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option} label={option} size="small" sx={chipSx} />
            ))
          }
          sx={{ minWidth: 160, flex: '1 1 140px', maxWidth: 260 }}
          limitTags={2}
        />

        {/* Store multi-select (cascaded by city) */}
        <Autocomplete
          multiple
          size="small"
          options={storeOptions}
          value={storeOptions.filter((s) => stores.includes(s.store_id))}
          onChange={(_, val) => setStores(val.map((s) => s.store_id))}
          getOptionLabel={(opt) => opt.store_name}
          isOptionEqualToValue={(opt, val) => opt.store_id === val.store_id}
          loading={isLoading}
          renderInput={(params) => (
            <TextField {...params} label="Store" variant="outlined" sx={fieldSx} />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option.store_id} label={option.store_name} size="small" sx={chipSx} />
            ))
          }
          sx={{ minWidth: 200, flex: '1 1 180px', maxWidth: 320 }}
          limitTags={1}
        />

        {/* Date range toggle */}
        <Tooltip title={showDateRange ? 'Hide date range' : 'Set date range'}>
          <IconButton
            size="small"
            onClick={() => setShowDateRange((prev) => !prev)}
            sx={{
              color: dateRange.start || dateRange.end ? 'var(--color-accent)' : 'var(--color-text-muted)',
              '&:hover': { color: 'var(--color-accent)' },
            }}
          >
            <CalendarIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        {/* Clear all */}
        {activeFilterCount > 0 && (
          <Tooltip title="Reset all filters">
            <IconButton
              size="small"
              onClick={clearAll}
              sx={{ color: 'var(--color-text-muted)', '&:hover': { color: 'var(--color-danger)' } }}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {isLoading && <CircularProgress size={16} sx={{ color: 'var(--color-accent)' }} />}
      </Box>

      {/* Date range row (collapsible) */}
      <Collapse in={showDateRange}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 1,
              bgcolor: 'var(--color-bg-secondary)',
              borderTop: '1px solid var(--color-border-subtle)',
            }}
          >
            <DatePicker
              label="Start Date"
              value={dateRange.start}
              onChange={(val) => setDateRange({ ...dateRange, start: val })}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: fieldSx,
                  style: { width: 160 },
                },
              }}
            />
            <DatePicker
              label="End Date"
              value={dateRange.end}
              onChange={(val) => setDateRange({ ...dateRange, end: val })}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: fieldSx,
                  style: { width: 160 },
                },
              }}
            />
            {(dateRange.start || dateRange.end) && (
              <Tooltip title="Clear date range">
                <IconButton
                  size="small"
                  onClick={() => setDateRange({ start: null, end: null })}
                  sx={{ color: 'var(--color-text-muted)', '&:hover': { color: 'var(--color-danger)' } }}
                >
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </LocalizationProvider>
      </Collapse>
    </Box>
  );
};

export default ForecastingFilterBar;
